import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { fail } from 'src/utils/notify'
import { APIS, LOADERS } from 'src/config/constants'
import { useLoader } from 'src/hooks/useLoader'
import type {
  IPaginatedNotificationResult,
  IReceivedNotification,
} from 'src/interfaces/notification'

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<IReceivedNotification[]>([])
  const notificationCount = ref(0)
  const isNotificationsLoading = ref(false)
  const isLoadingMore = ref(false)
  const isCountLoading = ref(false)
  const isDismissingNotificationPending = ref(false)
  const isNotificationsPanelOpen = ref(false)
  const continuationToken = ref<string | null>(null)
  const hasMore = ref(false)
  const notificationAnimationTrigger = ref(0)
  const updatingPinnedNotificationIds = ref<string[]>([])
  const isPinnedNotificationsExpanded = ref(true)

  const hasNotifications = computed(() => notificationCount.value > 0)
  const displayCount = computed(() =>
    notificationCount.value > 99 ? '99+' : notificationCount.value,
  )

  const { useFetchLoader: fetchNotificationsList } =
    useLoader<IPaginatedNotificationResult>(LOADERS.notification, {
      uri: `${APIS.inapp}/${APIS.notifications}`,
    })

  const { useFetchLoader: fetchNotificationsCountLoader } = useLoader<number>(
    LOADERS.notification,
    {
      uri: `${APIS.inapp}/${APIS.notifications}/${APIS.count}`,
    },
  )

  const { usePostLoader: clearNotificationsLoader } = useLoader<void>(
    LOADERS.notification,
    {
      uri: `${APIS.inapp}/${APIS.notifications}/${APIS.clear}`,
    },
  )

  const { usePostLoader: clearAllNotificationsLoader } = useLoader<void>(
    LOADERS.notification,
    {
      uri: `${APIS.inapp}/${APIS.notifications}/${APIS.clearAll}`,
    },
  )

  const { usePostLoader: undoClearAllNotificationsLoader } = useLoader<void>(
    LOADERS.notification,
    {
      uri: `${APIS.inapp}/${APIS.notifications}/${APIS.undoClearAll}`,
    },
  )

  const { usePostLoader: undoClearNotificationsLoader } = useLoader<void>(
    LOADERS.notification,
    {
      uri: `${APIS.inapp}/${APIS.notifications}/${APIS.undo}`,
    },
  )

  const { usePostLoader: pinNotificationLoader, error: pinNotificationError } =
    useLoader<void>(LOADERS.notification, {
      uri: `${APIS.inapp}/${APIS.notifications}/${APIS.pinLower}`,
    })

  const {
    usePostLoader: unpinNotificationLoader,
    error: unpinNotificationError,
  } = useLoader<void>(LOADERS.notification, {
    uri: `${APIS.inapp}/${APIS.notifications}/${APIS.unpin}`,
  })

  async function fetchNotifications(reset = true) {
    // Prevent multiple simultaneous calls
    if (reset && isNotificationsLoading.value) {
      return
    }
    if (!reset && isLoadingMore.value) {
      return
    }

    if (reset) {
      isNotificationsLoading.value = true
      continuationToken.value = null
    } else {
      isLoadingMore.value = true
    }

    try {
      const params: Record<string, string> = {
        pageSize: '15',
      }
      if (continuationToken.value && !reset) {
        params.continuationToken = continuationToken.value
      }

      const result = await fetchNotificationsList({ params })

      if (result !== undefined && result !== null) {
        if (reset) {
          notifications.value = sortNotifications(result.items)
        } else {
          notifications.value = sortNotifications([
            ...notifications.value,
            ...result.items,
          ])
        }

        continuationToken.value = result.continuationToken
        hasMore.value = result.hasMore
      }
    } finally {
      isNotificationsLoading.value = false
      isLoadingMore.value = false
    }
  }

  async function loadMoreNotifications() {
    if (
      hasMore.value &&
      !isLoadingMore.value &&
      !isNotificationsLoading.value
    ) {
      await fetchNotifications(false)
    }
  }

  async function fetchNotificationCount() {
    isCountLoading.value = true
    try {
      const result = await fetchNotificationsCountLoader({})
      notificationCount.value = result || 0
    } finally {
      isCountLoading.value = false
    }
  }

  function handleNotificationReceived() {
    notificationCount.value++
    notificationAnimationTrigger.value++

    if (isNotificationsPanelOpen.value) {
      void fetchNotifications(true)
    }
  }

  async function refreshNotificationState() {
    await fetchNotificationCount()
    if (isNotificationsPanelOpen.value) {
      await fetchNotifications(true)
    }
  }

  async function setNotificationsPanelOpen(isOpen: boolean) {
    isNotificationsPanelOpen.value = isOpen
    if (isOpen) {
      await fetchNotifications(true)
    }
  }

  function clearNotifications() {
    notifications.value = []
    notificationCount.value = 0
  }

  async function dismissNotification(id: string, callback?: () => void) {
    isDismissingNotificationPending.value = true
    try {
      await clearNotificationsLoader({
        NotificationIds: [id],
      })
      notifications.value = notifications.value.filter(
        (notification) => notification.id !== id,
      )
      notificationCount.value = Math.max(0, notificationCount.value - 1)
      if (callback) callback()
    } catch (error: any) {
      fail(
        error?.response?.data?.message
          ? error?.response?.data?.message
          : error.message,
      )
    } finally {
      isDismissingNotificationPending.value = false
    }
  }

  async function clearAllNotifications() {
    isDismissingNotificationPending.value = true
    try {
      await clearAllNotificationsLoader({})
      const persistentNotifications = notifications.value.filter(
        (n) => n.isPersistent || n.isPinned,
      )
      notifications.value = persistentNotifications
      notificationCount.value = persistentNotifications.length
    } catch (error: any) {
      fail(
        error?.response?.data?.message
          ? error?.response?.data?.message
          : error.message,
      )
    } finally {
      isDismissingNotificationPending.value = false
    }
  }

  function notificationTime(notification: IReceivedNotification): number {
    const timestamp = new Date(notification.createdUtc).getTime()
    return Number.isNaN(timestamp) ? 0 : timestamp
  }

  function sortNotifications(
    notificationsToSort: IReceivedNotification[],
  ): IReceivedNotification[] {
    return [...notificationsToSort].sort((a, b) => {
      const aIsPinned = a.isPinned === true
      const bIsPinned = b.isPinned === true
      if (aIsPinned !== bIsPinned) return aIsPinned ? -1 : 1

      const timeDifference = notificationTime(b) - notificationTime(a)
      if (timeDifference !== 0) return timeDifference

      return a.id.localeCompare(b.id)
    })
  }

  async function undoClearAllNotifications(
    clearedNotifications: IReceivedNotification[],
    restoredCount?: number,
  ) {
    isDismissingNotificationPending.value = true
    try {
      await undoClearAllNotificationsLoader({})
      const combined = [...clearedNotifications, ...notifications.value]
      notifications.value = sortNotifications(combined)
      notificationCount.value = restoredCount ?? clearedNotifications.length
    } catch (error: any) {
      fail(
        error?.response?.data?.message
          ? error?.response?.data?.message
          : error.message,
      )
    } finally {
      isDismissingNotificationPending.value = false
    }
  }

  async function undoClearNotifications(
    clearedNotifications: IReceivedNotification[],
    skipFrontendRestore = false,
  ) {
    isDismissingNotificationPending.value = true
    try {
      const ids = clearedNotifications.map((n) => n.id)
      await undoClearNotificationsLoader({
        NotificationIds: ids,
      })

      if (!skipFrontendRestore) {
        const combined = [...clearedNotifications, ...notifications.value]
        notifications.value = sortNotifications(combined)
      }
      notificationCount.value =
        notificationCount.value + clearedNotifications.length
    } catch (error: any) {
      fail(
        error?.response?.data?.message
          ? error?.response?.data?.message
          : error.message,
      )
    } finally {
      isDismissingNotificationPending.value = false
    }
  }

  function clearEntity(entityId: string) {
    const countBeforeClear = notifications.value.length
    notifications.value = notifications.value.filter(
      (notification) => notification.entityId !== entityId,
    )
    const clearedCount = countBeforeClear - notifications.value.length
    notificationCount.value = Math.max(
      0,
      notificationCount.value - clearedCount,
    )
  }

  async function setNotificationPinned(
    notification: IReceivedNotification,
    isPinned: boolean,
  ) {
    if (updatingPinnedNotificationIds.value.includes(notification.id)) return

    updatingPinnedNotificationIds.value = [
      ...updatingPinnedNotificationIds.value,
      notification.id,
    ]
    try {
      const payload = { NotificationIds: [notification.id] }
      if (isPinned) {
        await pinNotificationLoader(payload)
        if (pinNotificationError.value) return
      } else {
        await unpinNotificationLoader(payload)
        if (unpinNotificationError.value) return
      }

      notification.isPinned = isPinned
      notifications.value = sortNotifications(notifications.value)
    } finally {
      updatingPinnedNotificationIds.value =
        updatingPinnedNotificationIds.value.filter(
          (id) => id !== notification.id,
        )
    }
  }

  return {
    notifications,
    notificationCount,
    displayCount,
    isNotificationsLoading,
    isLoadingMore,
    isCountLoading,
    isDismissingNotificationPending,
    isNotificationsPanelOpen,
    hasNotifications,
    hasMore,
    notificationAnimationTrigger,
    updatingPinnedNotificationIds,
    isPinnedNotificationsExpanded,
    fetchNotifications,
    fetchNotificationCount,
    loadMoreNotifications,
    handleNotificationReceived,
    refreshNotificationState,
    setNotificationsPanelOpen,
    clearNotifications,
    dismissNotification,
    clearAllNotifications,
    clearEntity,
    undoClearAllNotifications,
    undoClearNotifications,
    setNotificationPinned,
  }
})
